import moment from 'moment-timezone';
import { BillingMode } from '../exports/Constants';

/**
 * The date the subscription review step (billing/Agreement) shows as the next
 * bill for a live restaurant. It mirrors what the backend does with the trial
 * the dialog sends (SubscriptionsService::create / updatedTrialEnd /
 * keepsRunningTrial), so the review step and the subscription card agree:
 *
 * - new subscription: charged when its trial ends, or tomorrow without one
 *   (the backend gives a trial-less live subscription a one-day trial);
 * - existing subscription with no running trial: the backend ignores the
 *   dialog's trial days, so the next charge stays the subscription's own
 *   next_billing_date, the date the card shows;
 * - existing subscription on a running trial: the trial end when the save
 *   keeps the trial (see keepsRunningTrial), today when it ends the trial,
 *   today + days when it restarts it.
 *
 * Returns null when there is no date to show: the company is not live
 * (Agreement has its own "after launch" copy for that), the subscription is
 * paused, ending or not collecting, or it is an internal subscription off the
 * billing cycle, whose stored date is its last charge rather than the next.
 * See tests/services/helpers/SubscriptionBillDate.test.js.
 */
export function nextBillDate({ data, isLive, hasTrial, trialDays, now = moment() }) {
  if (!isLive) {
    return null;
  }

  // What the dialog sends as trial_days: hasTrial ? trialDays : null.
  const sentTrialDays = hasTrial ? toTrialDays(trialDays) : null;

  if (!data) {
    return now.clone().add(sentTrialDays || 1, 'days');
  }

  if (!data.is_active || data.is_resumable || !data.collects_payments) {
    return null;
  }

  const trialEndsAt = data.trial_ends_at ? moment(data.trial_ends_at) : null;

  if (!trialEndsAt?.isAfter(now)) {
    const knowsNextCharge = data.billing_mode === BillingMode.Stripe || data.is_cycle_aligned;

    return data.next_billing_date && knowsNextCharge ? moment(data.next_billing_date) : null;
  }

  if (keepsRunningTrial(trialEndsAt, toTrialDays(data.trial_days), sentTrialDays, now)) {
    return trialEndsAt;
  }

  return sentTrialDays > 0 ? now.clone().add(sentTrialDays, 'days') : now.clone();
}

/**
 * SubscriptionsService::keepsRunningTrial: a save keeps a running trial when
 * it sends the stored trial_days (null included: a subscription created before
 * go-live has none while its launch trial runs), or the days the dialog shows
 * for that trial, its whole remaining days + 1. Any other value restarts the
 * trial from now; null or 0 ends it now.
 */
function keepsRunningTrial(trialEndsAt, storedTrialDays, sentTrialDays, now) {
  if (sentTrialDays === storedTrialDays) {
    return true;
  }

  if (sentTrialDays === null) {
    return false;
  }

  return (
    trialEndsAt.isSameOrAfter(now.clone().add(sentTrialDays - 1, 'days')) &&
    trialEndsAt.isBefore(now.clone().add(sentTrialDays, 'days'))
  );
}

/**
 * An empty input sends no trial_days (null on the backend); a stored value
 * may come back as a string.
 */
function toTrialDays(value) {
  return value === null || value === undefined || value === '' ? null : Number(value);
}
